import React from 'react'
import {
  Card,
  Row,
  Col,
  Form as BForm,
  Button,
  InputGroup
} from '@themesberg/react-bootstrap'
import Select from 'react-select'

import LogoUpload from './UploadLogo'
import { Formik, Form, ErrorMessage } from 'formik'
import { tenantSchema, editTenantSchema } from '../schema'
import useTenant from '../hooks/useTenant'
import Preloader from '../../../../components/Preloader'
import { isDomainExist } from '../../../../utils/apiCalls'
import { toast } from '../../../../components/Toast'

export default ({ createTenantData, setCreateTenantData, setSelectedTab, isEdit = false }) => {
  const {
    tenantId,
    currencies,
    setCurrencies,
    currOptions,
    allCurrencies,
    navigate,
    loading,
    tenantDetails,
    setAllowedProviders,
    allowedLanguages,
    langOptions,
    allowedProviders,
    providerOptions,
    setAllowedLanguages
  } = useTenant({ createTenantData, setCreateTenantData })

  return (
    <>
      {loading
        ? <Preloader />
        : (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize
                initialValues={{
                  name: createTenantData.name,
                  domain: createTenantData.domain,
                  allowedCurrencies: createTenantData.allowedCurrencies,
                  primaryCurrency: createTenantData.primaryCurrency,
                  logo: createTenantData?.logo,
                  allowedLanguages: createTenantData.allowedLanguages,
                  providerIds: createTenantData.providerIds
                }}
                validationSchema={tenantId ? editTenantSchema(createTenantData?.logo) : tenantSchema}
                onSubmit={(formValues) => {
                  setCreateTenantData({
                    ...createTenantData,
                    ...formValues
                  })
                  setSelectedTab('credentials')
                }}
              >
                {({
                  touched,
                  errors,
                  values,
                  setSubmitting,
                  handleChange,
                  handleSubmit,
                  handleBlur,
                  setFieldValue
                }) => (
                  <Form>
                    <Row className='g-2 mt-3'>
                      {/* Col for Name */}
                      <Col>
                        <label
                          htmlFor='name'
                          className={
                            touched.name && errors.name ? 'text-danger' : ''
                          }
                        >
                          Name<span className='text-danger'> *</span>
                        </label>
                        <InputGroup
                          className={
                            touched.name && errors.name
                              ? 'border border-danger'
                              : ''
                          }
                        >
                          <BForm.Control
                            name='name'
                            required
                            type='text'
                            disabled={isEdit}
                            placeholder='Enter Name'
                            value={values.name}
                            onInput={handleChange}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </InputGroup>
                        <ErrorMessage
                          component='div'
                          name='name'
                          className='text-danger'
                        />
                      </Col>

                      {/* Col for Domain */}
                      <Col>
                        <label
                          htmlFor='domain'
                          className={
                            touched.domain && errors.domain ? 'text-danger' : ''
                          }
                        >
                          {' '}
                          Domain<span className='text-danger'> *</span>
                        </label>
                        <InputGroup
                          className={
                            touched.domain && errors.domain
                              ? 'border border-danger'
                              : ''
                          }
                        >
                          <BForm.Control
                            name='domain'
                            required
                            type='url'
                            placeholder='Enter Domain'
                            value={values.domain}
                            onInput={handleChange}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </InputGroup>
                        <ErrorMessage
                          component='div'
                          name='domain'
                          className='text-danger'
                        />
                      </Col>
                    </Row>

                    <Row className='g-2 mt-3'>
                      {/* Col for Allowed Currencies */}
                      <Col>
                        <BForm.Group className='mb-3'>
                          <BForm.Label
                            className={
                              touched.allowedCurrencies && errors.allowedCurrencies ? 'text-danger' : ''
                            }
                          >
                            Allowed Currencies
                            <span className='text-danger'> *</span>
                          </BForm.Label>

                          <Select
                            isMulti
                            isClearable={false}
                            name='allowedCurrencies'
                            value={currencies}
                            options={currOptions}
                            className='basic-multi-select'
                            classNamePrefix='select'
                            onChange={(option, e) => {
                              if (
                                tenantDetails?.tenantConfig?.allowedCurrencies?.includes(
                                  e?.removedValue?.value
                                ) || e?.removedValue?.value === 'EUR'
                              ) { return }
                              setCurrencies(option)
                              setFieldValue(
                                'allowedCurrencies',
                                option.map((o) => o.value)
                              )
                            }}
                          />

                          <ErrorMessage
                            component='div'
                            name='allowedCurrencies'
                            className='text-danger'
                          />
                        </BForm.Group>

                        <Row>
                          <Col>
                            {values.allowedCurrencies.length !== 0 &&
                              (
                                <BForm.Group className='mb-3'>
                                  <BForm.Label
                                    className={
                                      touched.primaryCurrency && errors.primaryCurrency ? 'text-danger' : ''
                                    }
                                  >
                                    Primary Currency
                                    <span className='text-danger'> *</span>
                                  </BForm.Label>

                                  <BForm.Select
                                    name='primaryCurrency'
                                    onChange={handleChange}
                                    value={values.primaryCurrency}
                                    disabled={tenantId}
                                    arial-label='Select Primary Currency'
                                  >
                                    <option>Select Primary Currency</option>
                                    {values.allowedCurrencies.map(
                                      (currency, index) => {
                                        const curr =
                                    allCurrencies &&
                                    allCurrencies?.rows.find(
                                      (c) => c.code === currency
                                    )

                                        return (
                                          <option
                                            key={index}
                                            value={curr && curr.code}
                                          >
                                            {curr && curr.name}
                                          </option>
                                        )
                                      }
                                    )}
                                  </BForm.Select>

                                  <ErrorMessage
                                    component='div'
                                    name='primaryCurrency'
                                    className='text-danger'
                                  />
                                </BForm.Group>
                              )}
                          </Col>
                        </Row>

                      </Col>
                      {/* Col for Logo */}
                      <Col>
                        <BForm.Group className='mb-3' controlId='formBasicEmail'>
                          <BForm.Label>
                            Logo<span className='text-danger'> *</span>
                          </BForm.Label>

                          <BForm.Text>
                            <LogoUpload
                              createTenantData={createTenantData}
                              setFieldValue={setFieldValue}
                            />
                          </BForm.Text>
                          <ErrorMessage
                            component='div'
                            name='logo'
                            className='text-danger'
                          />
                        </BForm.Group>
                      </Col>
                    </Row>

                    <Row>
                      {/* Col for Allowed Languages */}
                      <Col>
                        <BForm.Group className='mb-3'>
                          <BForm.Label
                            className={
                              touched.allowedLanguages && errors.allowedLanguages ? 'text-danger' : ''
                            }
                          >
                            Allowed Languages
                            <span className='text-danger'> *</span>
                          </BForm.Label>

                          <Select
                            isMulti
                            isClearable={false}
                            name='allowedLanguages'
                            value={allowedLanguages}
                            options={langOptions}
                            className='basic-multi-select'
                            classNamePrefix='select'
                            onChange={(option, e) => {
                              if (
                                tenantDetails?.tenantConfig?.allowedLanguages?.includes(
                                  e?.removedValue?.value
                                ) || e?.removedValue?.value === 'EN'
                              ) { return }
                              setAllowedLanguages(option)
                              setFieldValue(
                                'allowedLanguages',
                                option.map((o) => o.value)
                              )
                            }}
                          />

                          <ErrorMessage
                            component='div'
                            name='allowedLanguages'
                            className='text-danger'
                          />
                        </BForm.Group>
                      </Col>

                      {/* Col for Allowed Providers */}
                      <Col>
                        <BForm.Group className='mb-3'>
                          <BForm.Label>
                            Allowed Providers
                            <span className='text-danger'> *</span>
                          </BForm.Label>

                          <Select
                            isMulti
                            isClearable={false}
                            name='providerIds'
                            value={allowedProviders}
                            options={providerOptions}
                            className='basic-multi-select'
                            classNamePrefix='select'
                            onChange={(option, e) => {
                              if (
                                tenantDetails?.tenantConfig?.gameProviders?.includes(
                                  e?.removedValue?.value
                                )
                              ) { return }
                              setAllowedProviders(option)
                              setFieldValue(
                                'providerIds',
                                option.map((o) => o.value)
                              )
                            }}
                          />
                        </BForm.Group>
                      </Col>
                    </Row>

                    <div className='d-flex justify-content-between mt-3'>
                      <Button
                        onClick={() => navigate(-1)}
                        variant='outline-warning'
                      >
                        Cancel
                      </Button>

                      <Button
                        onClick={() => {
                          if (createTenantData.domain !== values.domain) {
                            isDomainExist({ domain: values.domain, tenantId: tenantId || '' })
                              .then(() => handleSubmit())
                              .catch(
                                err => {
                                  setSubmitting(false)
                                  toast(err.response.data.message, 'error')
                                }
                              )
                          } else {
                            handleSubmit()
                          }
                        }}
                        variant='outline-success'
                      >
                        Next
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>)}
    </>
  )
}
